import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  horizontalListSortingStrategy,
  SortableContext,
} from "@dnd-kit/sortable";
import { observer, useLocalObservable } from "mobx-react-lite";
import { forwardRef, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import {
  BRANCH_MEDIA_ACCEPTED_TYPES,
  BranchMediaValidation,
} from "@/api/admin/branches";
import { commonApi } from "@/api/common";
import {
  CustomCardBody,
  ErrorForm,
  SimpleText,
  Subtitle,
} from "@/components/atoms";
import { Form } from "@/components/ui/form";
import { cn } from "@/lib/utils";
import type { DemoBranchStore } from "@/modules/BranchStepsAdmin/DemoBranchStore";
import { useWizardStepDraft } from "@/modules/wizardDraft";
import type { FormProps, FormRefType } from "@/types/common";
import { useExposeFormValues } from "@/utils/hooks/useExposeFormValues";

import { MediaStore, MediaUploader, UploadedFiles } from "../MediaUploader";
import { type BranchMediaFormType } from "./types";

type BranchMediaFormProps = FormProps<BranchMediaFormType> & {
  isTitle?: boolean;
  branchStore?: DemoBranchStore;
  onPendingChange?: (pending: boolean) => void;
  draftEntityId?: string;
};

const BranchMediaFormInner = forwardRef<
  FormRefType<BranchMediaFormType>,
  BranchMediaFormProps
>(
  (
    {
      defaultValues,
      onSubmit,
      onValidityChange,
      id,
      classes,
      isTitle = true,
      branchStore,
      onPendingChange,
      draftEntityId,
    },
    ref
  ) => {
    const form = useForm<BranchMediaFormType>({
      defaultValues,
      mode: "onChange",
      reValidateMode: "onBlur",
    });

    useWizardStepDraft({
      form,
      wizard: "branch",
      entityId: draftEntityId,
      step: "media",
    });

    const mediaStore = useLocalObservable(
      () => new MediaStore(undefined, BranchMediaValidation)
    );

    useEffect(() => {
      if (defaultValues?.media) {
        const mediaFiles = defaultValues.media.map(media => {
          const originalUrl = media.variants[0].url;
          return {
            id: media.id,
            previewUrl: originalUrl,
            type: media.type,
          };
        });

        mediaStore.restoreFromPayload(mediaFiles);
      }
    }, [defaultValues, mediaStore]);

    const [errorMedia, setErrorMedia] = useState<string | null>(null);

    const {
      formState: { isValid },
      handleSubmit,
    } = form;

    useExposeFormValues(ref, form);

    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    const handleSubmitWithMedia = async () => {
      try {
        if (branchStore) {
          branchStore.isCreating = true;
        }

        onPendingChange?.(true);

        const mediaItems = await Promise.all(
          mediaStore.mediaFiles.map(async f => {
            if (f.file) {
              try {
                const response = await commonApi.mediaUpload(f.file);
                return { mediaId: response.id, processedMediaId: undefined };
              } catch (err) {
                console.error("Upload failed for file", f.file.name, err);
                return {
                  mediaId: null,
                  processedMediaId: undefined,
                  error: true,
                };
              }
            } else if (f.id) {
              return {
                mediaId: undefined,
                processedMediaId: f.id,
              };
            }
            return { mediaId: undefined, processedMediaId: undefined };
          })
        );

        const payload: BranchMediaFormType = {
          mediaItems: mediaItems.map(item => ({
            mediaId: item.mediaId ?? "",
            processedMediaId: item.processedMediaId ?? "",
          })),
        };

        onSubmit(payload);
      } catch (error) {
        console.error("Error:", error);
        setErrorMedia("Error in uploading");
      } finally {
        if (branchStore) {
          branchStore.isCreating = false;
        }
        onPendingChange?.(false);
        mediaStore.resetStore();
      }
    };

    const sensors = useSensors(useSensor(PointerSensor));

    const handleDragEnd = (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = mediaStore.mediaFiles.findIndex(m => m.id === active.id);
      const newIndex = mediaStore.mediaFiles.findIndex(m => m.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        mediaStore.moveMedia(oldIndex, newIndex);
      }
    };

    return (
      <Form {...form}>
        <form onSubmit={handleSubmit(handleSubmitWithMedia)} id={id}>
          <CustomCardBody classes={cn(classes)}>
            <div className="flex flex-col gap-8">
              <div>
                {isTitle && <Subtitle classes="mb-1" title="Media" />}
                <SimpleText classes="mb-10">
                  <span className="font-bold">Photo requirements:</span> use a
                  JPG, GIF, or PNG. Keep it under 3 MB for faster loading.
                  Dimensions: Image (700X700px).
                  <span className="font-bold">Video requirement:</span> For best
                  results, uploads should be at least 1080p (1920 x 1080 pixels)
                  in MP4 format. Keep it under 100 MB
                </SimpleText>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={mediaStore.mediaFiles.map(m => m.id!)}
                    strategy={horizontalListSortingStrategy}
                  >
                    <div className="flex gap-2 flex-wrap">
                      {mediaStore.mediaFiles.map((media, index) => (
                        <UploadedFiles
                          key={media.id}
                          media={media}
                          index={index}
                          mediaStore={mediaStore}
                        />
                      ))}
                      <MediaUploader
                        acceptedTypes={BRANCH_MEDIA_ACCEPTED_TYPES}
                        disabled={mediaStore.isTotalLimitReached}
                        mediaStore={mediaStore}
                        onError={setErrorMedia}
                      />
                    </div>
                  </SortableContext>
                </DndContext>

                {errorMedia && <ErrorForm message={errorMedia} />}
              </div>
            </div>
          </CustomCardBody>
        </form>
      </Form>
    );
  }
);

BranchMediaFormInner.displayName = "BranchMediaForm";

const BranchMediaForm = observer(BranchMediaFormInner);

export default BranchMediaForm;
